import type { CandidateOption, CandidateSpan } from "@fluentry/domain";

type SelectionRange = {
  start: number;
  end: number;
};

type CandidateLayer = {
  visibleSpans: CandidateSpan[];
  primarySpan: CandidateSpan | null;
  competingSpans: CandidateSpan[];
};

type TextDiff = {
  previousStart: number;
  previousEnd: number;
  nextEnd: number;
  insertedText: string;
};

export function getActiveCandidateLayer(
  candidateSpans: CandidateSpan[],
  selection: SelectionRange | null,
  preferredSpanId?: string | null,
): CandidateLayer {
  const validSpans = candidateSpans.filter(
    (span) => span.status !== "invalidated" && span.status !== "applied",
  );
  const intersectingSpans = selection
    ? validSpans.filter((span) => intersects(span, selection))
    : [];

  const preferredSpan =
    preferredSpanId != null
      ? (intersectingSpans.find((span) => span.id === preferredSpanId) ??
        validSpans.find((span) => span.id === preferredSpanId) ??
        null)
      : null;

  const primarySpan = preferredSpan ?? pickPrimarySpan(intersectingSpans) ?? null;
  const competingSpans = primarySpan
    ? intersectingSpans.filter(
        (span) =>
          span.id !== primarySpan.id &&
          rangesOverlap(span.start, span.end, primarySpan.start, primarySpan.end),
      )
    : [];
  const visibleSpans = buildVisibleLayer(validSpans, primarySpan);

  return {
    visibleSpans,
    primarySpan,
    competingSpans,
  };
}

export function applyCandidateReplacement(args: {
  document: string;
  candidateSpans: CandidateSpan[];
  spanId: string;
  candidate: CandidateOption;
}): {
  document: string;
  candidateSpans: CandidateSpan[];
} {
  const targetSpan = args.candidateSpans.find((span) => span.id === args.spanId);
  if (!targetSpan) {
    return {
      document: args.document,
      candidateSpans: args.candidateSpans,
    };
  }

  const nextDocument =
    args.document.slice(0, targetSpan.start) +
    args.candidate.text +
    args.document.slice(targetSpan.end);
  const delta = args.candidate.text.length - (targetSpan.end - targetSpan.start);

  const candidateSpans = args.candidateSpans.map<CandidateSpan>((span) => {
    if (span.id === targetSpan.id) {
      return {
        ...span,
        end: span.start + args.candidate.text.length,
        sourceText: args.candidate.text,
        status: "applied" as const,
        appliedCandidateId: args.candidate.id,
      };
    }

    if (span.end <= targetSpan.start) {
      return span;
    }

    if (span.start >= targetSpan.end) {
      return {
        ...span,
        start: span.start + delta,
        end: span.end + delta,
      };
    }

    return {
      ...span,
      status: "invalidated" as const,
    };
  });

  return {
    document: nextDocument,
    candidateSpans: sortCandidateSpans(candidateSpans),
  };
}

export function rebaseCandidateSpansForDocumentChange(args: {
  previousDocument: string;
  nextDocument: string;
  candidateSpans: CandidateSpan[];
}): CandidateSpan[] {
  if (args.previousDocument === args.nextDocument) {
    return sortCandidateSpans(args.candidateSpans);
  }

  const diff = detectTextDiff(args.previousDocument, args.nextDocument);
  if (!diff) {
    return sortCandidateSpans(
      args.candidateSpans.map((span) => ({
        ...span,
        status: span.status === "applied" ? span.status : "invalidated",
      })),
    );
  }

  const removedStart = diff.previousStart;
  const removedEnd = diff.previousEnd;
  const insertedLength = diff.insertedText.length;
  const removedLength = removedEnd - removedStart;
  const delta = insertedLength - removedLength;

  return sortCandidateSpans(
    args.candidateSpans.map<CandidateSpan>((span) => {
      if (span.end <= removedStart) {
        return span;
      }

      if (span.start >= removedEnd) {
        const shiftedSpan = {
          ...span,
          start: span.start + delta,
          end: span.end + delta,
        };
        return reanchorSpanIfNeeded(shiftedSpan, args.nextDocument) ?? shiftedSpan;
      }

      return (
        reanchorSpanIfNeeded(span, args.nextDocument) ?? {
          ...span,
          status: span.status === "applied" ? span.status : "invalidated",
        }
      );
    }),
  );
}

function pickPrimarySpan(spans: CandidateSpan[]) {
  return [...spans].sort((left, right) => {
    const leftLength = left.end - left.start;
    const rightLength = right.end - right.start;
    if (leftLength !== rightLength) {
      return leftLength - rightLength;
    }
    if (left.start !== right.start) {
      return left.start - right.start;
    }
    return left.id.localeCompare(right.id);
  })[0];
}

function buildVisibleLayer(spans: CandidateSpan[], primarySpan: CandidateSpan | null) {
  const sorted = [...spans].sort((left, right) => {
    if (left.start !== right.start) {
      return left.start - right.start;
    }
    return left.end - right.end;
  });

  const visible: CandidateSpan[] = [];
  for (const span of sorted) {
    if (
      primarySpan &&
      span.id !== primarySpan.id &&
      rangesOverlap(span.start, span.end, primarySpan.start, primarySpan.end)
    ) {
      continue;
    }

    if (visible.some((entry) => rangesOverlap(entry.start, entry.end, span.start, span.end))) {
      continue;
    }

    visible.push(span);
  }

  if (primarySpan && !visible.some((span) => span.id === primarySpan.id)) {
    visible.push(primarySpan);
  }

  return visible.sort((left, right) => left.start - right.start || left.end - right.end);
}

function intersects(span: CandidateSpan, selection: SelectionRange) {
  if (selection.start === selection.end) {
    return span.start <= selection.start && selection.start < span.end;
  }

  return rangesOverlap(span.start, span.end, selection.start, selection.end);
}

function rangesOverlap(startA: number, endA: number, startB: number, endB: number) {
  return startA < endB && startB < endA;
}

function detectTextDiff(previousDocument: string, nextDocument: string): TextDiff | null {
  const prefixLength = sharedPrefixLength(previousDocument, nextDocument);
  const suffixLength = sharedSuffixLength(previousDocument, nextDocument, prefixLength);

  return {
    previousStart: prefixLength,
    previousEnd: previousDocument.length - suffixLength,
    nextEnd: nextDocument.length - suffixLength,
    insertedText: nextDocument.slice(prefixLength, nextDocument.length - suffixLength),
  };
}

function reanchorSpanIfNeeded(span: CandidateSpan, nextDocument: string): CandidateSpan | null {
  const exactMatches: number[] = [];
  let searchIndex = 0;

  while (searchIndex <= nextDocument.length) {
    const matchIndex = nextDocument.indexOf(span.sourceText, searchIndex);
    if (matchIndex === -1) {
      break;
    }

    exactMatches.push(matchIndex);
    if (exactMatches.length > 1) {
      return null;
    }

    searchIndex = matchIndex + 1;
  }

  if (exactMatches.length !== 1) {
    return null;
  }

  const start = exactMatches[0];
  return {
    ...span,
    start,
    end: start + span.sourceText.length,
  };
}

function sortCandidateSpans(spans: CandidateSpan[]) {
  return [...spans].sort((left, right) => {
    if (left.start !== right.start) {
      return left.start - right.start;
    }

    if (left.end !== right.end) {
      return left.end - right.end;
    }

    return left.id.localeCompare(right.id);
  });
}

function sharedPrefixLength(left: string, right: string) {
  const limit = Math.min(left.length, right.length);
  let index = 0;
  while (index < limit && left[index] === right[index]) {
    index += 1;
  }
  return index;
}

function sharedSuffixLength(left: string, right: string, prefixLength: number) {
  const leftRemaining = left.length - prefixLength;
  const rightRemaining = right.length - prefixLength;
  const limit = Math.min(leftRemaining, rightRemaining);
  let index = 0;
  while (index < limit && left[left.length - 1 - index] === right[right.length - 1 - index]) {
    index += 1;
  }
  return index;
}
